import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, MapPin, Globe, Languages, Award, Lock, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta, initials } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";

export default function CoachProfile() {
  const { id } = useParams();
  const { isAuthed } = useAuth();
  const t = useT();
  const [coach, setCoach] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCoach(undefined);
    api.get(`/public/coaches/${id}`)
      .then((r) => setCoach(r.data))
      .catch(() => setCoach(null));
  }, [id]);

  usePageMeta(coach?.name || t("cp.meta_fallback"), coach?.bio);

  if (coach === undefined) {
    return (
      <div className="container-page py-12">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="mt-6 h-8 w-64" />
        <Skeleton className="mt-3 h-24 w-full" />
      </div>
    );
  }
  if (coach === null) {
    return (
      <div className="container-page py-20">
        <EmptyState icon={UserX} title={t("cp.notfound_title")} description={t("cp.notfound_desc")}
          action={<Button className="rounded-full" asChild><Link to="/coaches">{t("cp.all_coaches")}</Link></Button>} />
      </div>
    );
  }

  const professions = coach.professions?.length ? coach.professions : coach.profession ? [coach.profession] : [];

  return (
    <div>
      <div className="relative h-52 bg-accent sm:h-64">
        {coach.header_image ? (
          <img src={coach.header_image} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-sage-pale to-oat-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
      </div>

      <div className="container-page">
        <Link to="/coaches" className="mt-5 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("cp.all_coaches")}
        </Link>

        <div className="relative -mt-16 flex flex-col gap-5 sm:-mt-20 sm:flex-row sm:items-end">
          <Avatar className="h-28 w-28 border-4 border-background shadow-md sm:h-32 sm:w-32">
            <AvatarImage src={coach.photo} alt={coach.name} />
            <AvatarFallback className="bg-primary text-2xl text-primary-foreground">{initials(coach.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 pb-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl text-foreground">{coach.name}</h1>
              {coach.verified ? <BadgeCheck className="h-6 w-6 text-primary" /> : null}
            </div>
            {professions.length ? <p className="mt-1 text-muted-foreground">{professions.join(" · ")}</p> : null}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {(coach.location || coach.region) && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {coach.location || coach.region}</span>}
              {coach.career_start_year && <span className="inline-flex items-center gap-1"><Award className="h-4 w-4" /> {t("cp.since_year", { year: coach.career_start_year })}</span>}
              {coach.languages_spoken?.length ? <span className="inline-flex items-center gap-1"><Languages className="h-4 w-4" /> {coach.languages_spoken.join(", ")}</span> : null}
            </div>
          </div>
          <div className="pb-1">
            <Button size="lg" className="w-full rounded-full sm:w-auto" asChild data-testid="coach-cta">
              <Link to={isAuthed ? `/app/session-booking?coach_id=${id}` : "/signup"}>{isAuthed ? t("cp.cta_book") : t("cp.cta_signup")}</Link>
            </Button>
          </div>
        </div>

        <div className="mt-10 grid gap-10 pb-16 lg:grid-cols-[1.6fr_1fr]">
          <div className="space-y-10">
            {coach.bio ? (
              <section>
                <h2 className="mb-3 font-display text-2xl text-foreground">{t("cp.about", { name: coach.name.split(" ")[0] })}</h2>
                <p className="whitespace-pre-line text-[15px] leading-relaxed text-foreground/90">{coach.bio}</p>
              </section>
            ) : null}

            <section>
              <h2 className="mb-3 font-display text-2xl text-foreground">{t("cp.posts")}</h2>
              {coach.free_posts?.length ? (
                <div className="space-y-3">
                  {coach.free_posts.map((p) => (
                    <div key={p.id} className="rounded-xl border border-border bg-card p-4">
                      {p.title ? <p className="font-medium text-foreground">{p.title}</p> : null}
                      <p className="mt-1 text-sm text-muted-foreground">{p.teaser}</p>
                    </div>
                  ))}
                  {coach.paid_content_count > 0 && (
                    <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                      <Lock className="h-4 w-4 shrink-0" />
                      <span>{t("cp.more_exclusive", { n: coach.paid_content_count })}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("cp.no_posts")}</p>
              )}
            </section>
          </div>

          <aside className="space-y-6">
            {coach.expertise?.length ? (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-foreground">{t("cp.focus")}</h3>
                <div className="flex flex-wrap gap-2">
                  {coach.expertise.map((e) => <Badge key={e} variant="secondary" className="rounded-full font-normal">{e}</Badge>)}
                </div>
              </div>
            ) : null}
            {coach.topics?.length ? (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-foreground">{t("cp.topics")}</h3>
                <div className="flex flex-wrap gap-2">
                  {coach.topics.map((t) => <Badge key={t} variant="outline" className="rounded-full font-normal">{t}</Badge>)}
                </div>
              </div>
            ) : null}
            {coach.certifications?.length ? (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-foreground">{t("cp.qualifications")}</h3>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {coach.certifications.map((c, i) => <li key={i} className="flex gap-2"><Award className="h-4 w-4 shrink-0 text-sage-light" /> {c}</li>)}
                </ul>
              </div>
            ) : null}
            {coach.website ? (
              <a href={coach.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                <Globe className="h-4 w-4" /> {t("cp.visit_website")}
              </a>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
